import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { workReports as api, customers as custApi, uploadReportPhoto, deleteReportPhoto, updateReportPhoto, reportPhotoUrl, apiError } from "@/lib/api";
import { dateInput } from "@/lib/format";
import { PageHeader, Card, Loading } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Camera, CheckCircle2, ImagePlus, Plus, ChevronUp, ChevronDown, Type, Images, Cloud, Check } from "lucide-react";

const EMPTY = { customer_id: "", property_address: "", assessment_date: dateInput(new Date()), status: "draft", title: "", subtitle: "" };
const uid = () => `s_${Math.random().toString(36).slice(2, 9)}`;

function legacySections(r) {
  const s = [];
  const add = (title, content) => { if (content && String(content).trim()) s.push({ id: uid(), type: "text", title, content }); };
  add("Scope of Assessment", r.scope);
  add("Methodology", r.methodology);
  add("Inspection Findings", r.findings);
  s.push({ id: uid(), type: "photos", title: "Site Photographs", content: "" });
  add("Summary", r.summary);
  add("Action Plan", r.action_plan);
  add("Conclusions", r.conclusions);
  add("Recommendations", r.recommendations);
  return s;
}

export default function ReportEditor() {
  const nav = useNavigate();
  const { id } = useParams();
  const [rid, setRid] = useState(id || null);
  const [ready, setReady] = useState(false);
  const [custs, setCusts] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [sections, setSections] = useState([{ id: uid(), type: "text", title: "Overview", content: "" }]);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
  const dirty = useRef(false);
  const timer = useRef(null);

  useEffect(() => {
    (async () => {
      const cs = await custApi.list(); setCusts(cs);
      if (rid) {
        const r = await api.get(rid);
        setForm({ ...EMPTY, ...r, assessment_date: dateInput(r.assessment_date) });
        setPhotos(r.photos || []);
        const secs = r.sections && r.sections.length ? r.sections : legacySections(r);
        setSections(secs);
      }
      setReady(true);
    })().catch((e) => { toast.error(apiError(e)); setReady(true); });
  }, [rid]);

  const persist = useCallback(async (extra = {}) => {
    if (!rid) return;
    setSaveState("saving");
    try {
      await api.update(rid, { ...form, sections, ...extra });
      setSaveState("saved"); dirty.current = false;
    } catch (e) { setSaveState("idle"); toast.error(apiError(e)); }
  }, [rid, form, sections]);

  // debounced autosave
  useEffect(() => {
    if (!ready || !rid || !dirty.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => persist(), 1200);
    return () => timer.current && clearTimeout(timer.current);
  }, [form, sections, ready, rid, persist]);

  const mark = () => { dirty.current = true; };
  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); mark(); };
  const pickCustomer = (cid) => { const c = custs.find((x) => x.id === cid); setForm({ ...form, customer_id: cid, property_address: form.property_address || (c?.address || "") }); mark(); };

  const createDraft = async () => {
    if (!form.customer_id) return toast.error("Please select a customer.");
    try {
      const r = await api.create({ ...form, sections, status: "draft" });
      setRid(r.id); window.history.replaceState({}, "", `/work-reports/${r.id}/edit`);
      setSaveState("saved"); toast.success(`Report created: ${r.number}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const complete = async () => {
    if (!rid) { await createDraft(); }
    if (!form.customer_id) return;
    await persist({ status: "completed" });
    nav(`/work-reports/${rid}`);
  };

  // sections
  const addSection = (type) => { setSections([...sections, { id: uid(), type, title: type === "photos" ? "Photographs" : "New Section", content: "" }]); mark(); };
  const updSection = (sid, patch) => { setSections(sections.map((s) => (s.id === sid ? { ...s, ...patch } : s))); mark(); };
  const rmSection = (sid) => { setSections(sections.filter((s) => s.id !== sid)); mark(); };
  const move = (idx, dir) => {
    const j = idx + dir; if (j < 0 || j >= sections.length) return;
    const next = [...sections]; [next[idx], next[j]] = [next[j], next[idx]]; setSections(next); mark();
  };

  const onPhoto = (sid) => async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!rid) { toast.error("Save the report first, then add photos."); return; }
    setUploading(true);
    try {
      for (const f of files) {
        if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(f.type)) { toast.error(`${f.name}: unsupported type`); continue; }
        const p = await uploadReportPhoto(rid, f, "", "", sid);
        setPhotos((prev) => [...prev, p]);
      }
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); e.target.value = ""; }
  };
  const rmPhoto = async (pid) => { try { await deleteReportPhoto(rid, pid); setPhotos((prev) => prev.filter((p) => p.id !== pid)); } catch (e) { toast.error(apiError(e)); } };
  const setCaption = async (pid, caption) => { setPhotos((prev) => prev.map((p) => (p.id === pid ? { ...p, caption } : p))); try { await updateReportPhoto(rid, pid, { caption }); } catch (e) { toast.error(apiError(e)); } };

  if (!ready) return <Loading />;

  // figure numbering across photo sections in order
  let figCounter = 0;
  const figFor = {};
  sections.forEach((s) => { if (s.type === "photos") photos.filter((p) => p.section_id === s.id).forEach((p) => { figCounter += 1; figFor[p.id] = figCounter; }); });

  return (
    <div className="pb-24">
      <button onClick={() => nav("/work-reports")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0B132B] mb-4" data-testid="report-editor-back"><ArrowLeft className="w-4 h-4" /> Back to reports</button>
      <div className="flex items-center justify-between">
        <PageHeader title={rid ? "Report Builder" : "New Report"} subtitle="Build a custom report with your own sections, photos and captions. Changes autosave." />
        <div className="text-xs text-slate-400 flex items-center gap-1" data-testid="autosave-indicator">
          {saveState === "saving" ? <><Cloud className="w-4 h-4 animate-pulse" /> Saving…</> : saveState === "saved" ? <><Check className="w-4 h-4 text-emerald-500" /> Saved</> : null}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* LEFT: builder */}
        <div className="space-y-4">
          <Card className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Report Title *</Label><Input value={form.title} onChange={set("title")} placeholder="e.g. Building Inspection Report" className="mt-1.5" data-testid="report-title" /></div>
              <div><Label>Report Subtitle</Label><Input value={form.subtitle} onChange={set("subtitle")} placeholder="Optional" className="mt-1.5" data-testid="report-subtitle" /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Customer *</Label>
                <Select value={form.customer_id} onValueChange={pickCustomer}>
                  <SelectTrigger className="mt-1.5" data-testid="report-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>{custs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Assessment Date</Label><Input type="date" value={form.assessment_date} onChange={set("assessment_date")} className="mt-1.5" data-testid="report-date" /></div>
            </div>
            <div><Label>Property / Service Address</Label><Input value={form.property_address} onChange={set("property_address")} className="mt-1.5" data-testid="report-property" /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => { setForm({ ...form, status: v }); mark(); }}>
                <SelectTrigger className="mt-1.5" data-testid="report-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["draft", "in_progress", "completed", "sent"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </Card>

          {sections.map((s, idx) => (
            <Card key={s.id} className="p-4" data-testid={`section-${s.id}`}>
              <div className="flex items-center gap-2 mb-2">
                {s.type === "photos" ? <Images className="w-4 h-4 text-[#0B4A40]" /> : <Type className="w-4 h-4 text-[#0B4A40]" />}
                <Input value={s.title} onChange={(e) => updSection(s.id, { title: e.target.value })} className="h-8 font-semibold" placeholder="Section title" data-testid={`section-title-${s.id}`} />
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`section-up-${s.id}`}><ChevronUp className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(idx, 1)} disabled={idx === sections.length - 1} data-testid={`section-down-${s.id}`}><ChevronDown className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-500" onClick={() => rmSection(s.id)} data-testid={`section-delete-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
              {s.type === "text" ? (
                <Textarea value={s.content} onChange={(e) => updSection(s.id, { content: e.target.value })} rows={4} placeholder="Write this section…" data-testid={`section-content-${s.id}`} />
              ) : (
                <PhotoSection sid={s.id} rid={rid} photos={photos.filter((p) => p.section_id === s.id)} figFor={figFor} uploading={uploading} onPhoto={onPhoto(s.id)} rmPhoto={rmPhoto} setCaption={setCaption} />
              )}
            </Card>
          ))}

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => addSection("text")} data-testid="add-text-section"><Plus className="w-4 h-4 mr-1" /> Text section</Button>
            <Button variant="outline" onClick={() => addSection("photos")} data-testid="add-photos-section"><ImagePlus className="w-4 h-4 mr-1" /> Photo section</Button>
          </div>
        </div>

        {/* RIGHT: live preview */}
        <div className="hidden xl:block">
          <div className="sticky top-4">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Live preview</div>
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 max-h-[80vh] overflow-y-auto" data-testid="report-preview">
              <div className="text-center border-b-2 border-[#0B4A40] pb-3">
                <div className="text-xl font-bold text-[#0B4A40]">{form.title || "Work Report"}</div>
                {form.subtitle && <div className="text-xs tracking-widest text-slate-500 mt-1">{form.subtitle}</div>}
              </div>
              <div className="text-xs text-slate-500 mt-3">
                <span className="font-semibold text-slate-700">{custs.find((c) => c.id === form.customer_id)?.name || "—"}</span>
                {form.property_address ? ` · ${form.property_address}` : ""} · {form.assessment_date}
              </div>
              {sections.map((s) => (
                <div key={s.id} className="mt-4">
                  <div className="bg-[#E7F0EA] text-[#0B4A40] text-sm font-semibold px-3 py-1.5 border-l-4 border-[#C9A227] rounded">{s.title || (s.type === "photos" ? "Photographs" : "Section")}</div>
                  {s.type === "text" ? (
                    <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{s.content || <span className="text-slate-300">Empty section</span>}</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {photos.filter((p) => p.section_id === s.id).map((p) => (
                        <div key={p.id} className="border border-slate-200 rounded-lg p-1.5">
                          <img src={reportPhotoUrl(rid, p.id)} alt="" className="w-full h-24 object-cover rounded" />
                          <div className="text-[10px] text-slate-500 mt-1"><b>Figure {figFor[p.id]}</b>{p.caption ? ` — ${p.caption}` : ""}</div>
                        </div>
                      ))}
                      {photos.filter((p) => p.section_id === s.id).length === 0 && <div className="text-xs text-slate-300 col-span-2">No photos</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 lg:left-[260px] right-0 bg-white/95 backdrop-blur border-t border-slate-200 p-3 flex gap-2 justify-end z-20">
        {!rid && <Button variant="outline" onClick={createDraft} data-testid="save-draft-report">Create Draft</Button>}
        {rid && <Button variant="outline" onClick={() => persist()} data-testid="save-draft-report">Save Now</Button>}
        <Button onClick={complete} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="complete-report"><CheckCircle2 className="w-4 h-4 mr-1.5" /> Save &amp; Complete</Button>
      </div>
    </div>
  );
}

function PhotoSection({ sid, rid, photos, figFor, uploading, onPhoto, rmPhoto, setCaption }) {
  const fileRef = useRef(null);
  const camRef = useRef(null);
  return (
    <div>
      {!rid && <p className="text-xs text-amber-600 mb-2">Create the draft first to enable photo uploads.</p>}
      <div className="flex gap-2 mb-3">
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPhoto} className="hidden" data-testid={`photo-input-${sid}`} />
        <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden" data-testid={`camera-input-${sid}`} />
        <Button variant="outline" size="sm" onClick={() => (rid ? camRef.current?.click() : toast.error("Create the draft first."))} disabled={uploading}><Camera className="w-4 h-4 mr-1.5" /> Take Photo</Button>
        <Button variant="outline" size="sm" onClick={() => (rid ? fileRef.current?.click() : toast.error("Create the draft first."))} disabled={uploading} data-testid={`add-photo-${sid}`}><ImagePlus className="w-4 h-4 mr-1.5" /> {uploading ? "Uploading…" : "Add Photos"}</Button>
      </div>
      {photos.length === 0 ? <p className="text-sm text-slate-400">No photographs in this section.</p> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {photos.map((p) => (
            <div key={p.id} className="border border-slate-200 rounded-lg p-2" data-testid={`report-photo-${p.id}`}>
              <img src={reportPhotoUrl(rid, p.id)} alt="" className="w-full h-36 object-contain bg-slate-50 rounded" />
              <div className="text-xs font-semibold text-[#0B4A40] mt-1.5">Figure {figFor[p.id]}</div>
              <div className="flex gap-2 mt-1">
                <Input defaultValue={p.caption} onBlur={(e) => setCaption(p.id, e.target.value)} placeholder="Caption" className="h-8 text-xs" data-testid={`photo-caption-${p.id}`} />
                <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-500" onClick={() => rmPhoto(p.id)} data-testid={`delete-photo-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
